// Saved audiences are references; only new manual audiences send a size.
export function audienceForPayload(audience, availableAudiences = []) {
    if (!audience || (audience.audience_size_id == null && audience.audience_id == null)) {
        return audience;
    }

    const idKey = audience.audience_size_id != null ? 'audience_size_id' : 'audience_id';
    const savedAudience = availableAudiences.find(option =>
        option[idKey] != null && String(option[idKey]) === String(audience[idKey])
    );
    return {
        type: 'existing_audience',
        audience_name: savedAudience?.audience_name ?? audience.audience_name ?? '',
        [idKey]: audience[idKey],
    };
}


// Keep full details in UI state; reduce them to an ID only at the API boundary.
export function resolveAudienceDetails(audience, availableAudiences = []) {
    if (!audience) return null;
    const key = audience.audience_size_id != null ? 'audience_size_id'
        : audience.audience_id != null ? 'audience_id' : null;
    const saved = key && availableAudiences.find(option =>
        option[key] != null && String(option[key]) === String(audience[key]));
    return { ...audience, ...(saved || {}) };
}

// Missing allocations means a legacy/definition audience has no geographic restriction.
export function audienceMarketIds(audience, availableAudiences = []) {
    const allocations = resolveAudienceDetails(audience, availableAudiences)?.market_allocations;
    if (!Array.isArray(allocations)) return null;
    const ids = new Set();
    allocations.forEach(province => {
        if (province.enabled === false || !(Number(province.audience_size) > 0)) return;
        ids.add(String(province.mkt_id).trim());
        (province.cmas || []).forEach(cma => {
            if (cma.enabled !== false && Number(cma.audience_size) > 0) ids.add(String(cma.mkt_id).trim());
        });
    });
    return ids;
}

export function audienceMarketValidation(audience, components = [], markets = []) {
    const selected = audienceMarketIds(audience);
    if (selected === null) return '';
    const names = new Map(markets.map(market => [market.market.trim().toLowerCase(), String(market.mkt_id).trim()]));
    const labels = new Map(markets.map(market => [String(market.mkt_id).trim(), market.market]));
    const excluded = new Set();
    components.forEach(component => {
        const market = String(component.market || '').trim();
        const code = String(component.mkt_id ?? component.market_id ?? names.get(market.toLowerCase()) ??
            (/^\d{2}(\d{3})?$/.test(market) ? market : '')).trim();
        if (code && !selected.has(code)) excluded.add(labels.get(code) || market || 'an unavailable market');
    });
    return excluded.size
        ? `Selected audience has no allocation in ${[...excluded].sort().join(', ')}. Include these markets in the audience or change the components before applying it.`
        : '';
}

export function audienceIdentity(audience) {
    if (!audience) return null;
    if (audience.audience_size_id != null) return JSON.stringify(['size', String(audience.audience_size_id)]);
    if (audience.audience_id != null) return JSON.stringify(['definition', String(audience.audience_id)]);
    const allocations = audience.market_allocations?.map(province => ({
        mkt_id: String(province.mkt_id), audience_size: province.audience_size,
        cmas: (province.cmas || []).map(cma => ({
            mkt_id: String(cma.mkt_id), audience_size: cma.audience_size,
        })).sort((a, b) => a.mkt_id.localeCompare(b.mkt_id)),
    })).sort((a, b) => a.mkt_id.localeCompare(b.mkt_id));
    return JSON.stringify(['manual', audience.audience_name, audience.audience_size, allocations || null]);
}
